import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Stack,
  Button,
  IconButton,
  TextField,
  MenuItem,
  Typography,
  Divider,
  Alert,
  Chip,
  Checkbox,
} from "@mui/material";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import Swal from "sweetalert2";
import { collectShippingPayment } from "api/financial";
import { getCustomerAccountByAdmin } from "api/wallet";
import { GetCustomerInfo } from "api/customers";
import { GetStationDetailById } from "api/stations";
import { playIncorrectSound } from "utils";

const CURRENCIES = ["THB", "LAK", "USD"];
const SYM = { THB: "฿", LAK: "₭", USD: "$" };
const MONO = "'Space Grotesk', ui-monospace, monospace";
const fmtCur = (n, c) => `${SYM[c] || ""}${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

// Floating-point slack for THB comparisons (well under a satang) — absorbs rounding
// noise from division/multiplication chains without masking real under/overpayments.
// Must match the backend's EPSILON_THB in controllers/financial.js.
const EPSILON_THB = 1e-6;

// Convert an amount in `currency` to THB. Rate convention matches the backend /
// SelfPickup: 1 THB = rate.LAK LAK; 1 USD = rate.USD THB.
function toTHB(amount, currency, rate) {
  const n = Number(amount) || 0;
  if (!rate) return currency === "THB" ? n : 0;
  if (currency === "THB") return n;
  if (currency === "LAK") return n / Number(rate.LAK);
  if (currency === "USD") return n * Number(rate.USD);
  return 0;
}

// Inverse of toTHB: convert a THB amount into `currency` for prefilling the input.
// LAK is rounded to the nearest 1000 (matches the fee display); THB/USD to 2dp.
function fromTHB(amountTHB, currency, rate) {
  const n = Number(amountTHB) || 0;
  if (!rate) return currency === "THB" ? Math.round(n * 100) / 100 : 0;
  if (currency === "THB") return Math.round(n * 100) / 100;
  if (currency === "LAK") return Math.round((n * Number(rate.LAK)) / 1000) * 1000;
  if (currency === "USD") return Math.round((n / Number(rate.USD)) * 100) / 100;
  return 0;
}

// One payment line = one currency + amount + its own evidence photos (the backend
// requires evidence per line). A method (cash / bank) can hold several lines so a
// customer can e.g. pay 500 THB cash and the remaining 150 THB worth in LAK cash.
const emptyLine = (currency = "THB") => ({ currency, amount: "", files: [] });
const emptyMethod = () => ({ enabled: false, lines: [emptyLine()] });
const methodTHB = (m, rate) => (m.enabled ? m.lines.reduce((s, l) => s + toTHB(l.amount, l.currency, rate), 0) : 0);

// Half of a LAK note (1,000) or half a USD cent, in THB — slack for a LAK/USD payment that
// converts to a non-round THB amount (e.g. $42.58 ≈ ฿1,362.56 for a ฿1,362.52 target).
// THB lines are exact. Mirrors halfRoundingStepTHB in backend/controllers/financial.js.
function paymentRoundingTHB(currency, rate) {
  if (!rate) return 0;
  if (currency === "LAK") return 500 / Number(rate.LAK);
  if (currency === "USD") return 0.005 * Number(rate.USD);
  return 0;
}

// Default flat delivery service fee (staff-editable) — matches mobile-staff's
// PickupDetailScreen.
const DEFAULT_DELIVERY_FEE_THB = 40;

// A change line for an overpayment: handed back as physical cash from this
// station's drawer, or credited straight to the customer wallet instead.
// Mirrors the backend's ShippingPayment.changeLines shape and mobile-staff's
// ShippingPaymentSheet.tsx. `sourceAuto` tracks whether `source` still follows the
// "settle outstanding debt" default (see applyDefaultChangeSource) or was picked
// by staff — separate from `auto`, which tracks the same thing for `amount`.
const emptyChangeLine = () => ({ source: "cash", currency: "THB", amount: "", auto: true, sourceAuto: true });

// Default every still-"sourceAuto" change line to a wallet credit when staff opted
// to settle the customer's outstanding debt with this overpayment (and the wallet
// can actually accept a credit); otherwise physical cash, same as before this
// feature existed. Lines staff has explicitly re-sourced are left alone.
function applyDefaultChangeSource(lines, settleDebt, walletActive) {
  const defaultSource = settleDebt && walletActive === true ? "wallet_credit" : "cash";
  return lines.map((l) => (l.sourceAuto ? { ...l, source: defaultSource } : l));
}

// Auto-fill the LAST change line with whatever remains of the change due after
// every other line. Only the last line auto-fills; earlier lines and any line
// staff typed into directly are left alone.
function recomputeAutoChangeLine(lines, dueTHB, rate) {
  if (lines.length === 0) return lines;
  const lastIdx = lines.length - 1;
  const last = lines[lastIdx];
  if (!last.auto) return lines;
  const othersTHB = lines.slice(0, lastIdx).reduce((s, l) => s + toTHB(l.amount, l.currency, rate), 0);
  const remainingTHB = Math.max(0, dueTHB - othersTHB);
  const suggested = rate ? fromTHB(remainingTHB, last.currency, rate) : 0;
  const amountStr = suggested > 0 ? String(suggested) : "";
  if (amountStr === last.amount) return lines;
  const next = lines.slice();
  next[lastIdx] = { ...last, amount: amountStr };
  return next;
}

// Cap on photos per evidence slot (delivery proof, and each payment method's
// evidence) — matches the backend's MAX_PHOTOS_PER_SLOT in collectShippingPayment.
const MAX_PHOTOS = 10;
// Fallback when the customer has no setting (or can't be loaded) — matches the
// backend default in Customer.requiredProofPhotos.
const DEFAULT_REQUIRED_PROOF_PHOTOS = 2;

// Accumulate newly-picked files onto an existing array, capped at MAX_PHOTOS.
// Staff can add photos across several picks (e.g. camera shots one at a time)
// as well as multi-select in one go.
function appendFiles(existing, fileList) {
  return [...existing, ...Array.from(fileList || [])].slice(0, MAX_PHOTOS);
}

// Collect shipping fees for the selected (switched-on) parcels. Supports a mixed
// payment: an optional cash line + an optional bank-transfer line, each with its
// own currency and a mandatory evidence photo; whatever is left over is charged to
// the customer wallet (the backend decides whether approval is needed). Also lets
// staff pick Pickup vs. Delivery here, adding a flat delivery service fee on top.
export default function ShippingPaymentDialog({
  open,
  onClose,
  customerId,
  stationId,
  packageIds,
  baseFeeTHB,
  currencyExchangeRate,
  onSuccess,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [cash, setCash] = useState(emptyMethod);
  const [bank, setBank] = useState(emptyMethod);
  // Proof the parcel(s) were physically handed over — required on every collection.
  // Multiple photos supported (cap MAX_PHOTOS), all sent to the customer on WhatsApp.
  const [proofFiles, setProofFiles] = useState([]);
  // How many hand-over photos this customer needs — Customer.requiredProofPhotos
  // (default 2), loaded when the dialog opens.
  const [customerRequiredPhotos, setCustomerRequiredPhotos] = useState(DEFAULT_REQUIRED_PROOF_PHOTOS);
  // Final stations can switch hand-over photos off (Station.requireHandOverPhotos) —
  // then they are optional (0 needed). Anything but an explicit false keeps them required.
  const [stationPhotosRequired, setStationPhotosRequired] = useState(true);
  const requiredPhotos = stationPhotosRequired ? customerRequiredPhotos : 0;
  // Final stations can also switch money collection off (Station.collectPayment) —
  // hand-over only: no fee/payment UI, nothing charged, submitted with no payment lines.
  const [collectionOff, setCollectionOff] = useState(false);
  // False until this open's station flags are loaded, so a previous station's flags never
  // apply and the dialog doesn't flash before an automatic hand-over (see `bypass`).
  const [stationLoaded, setStationLoaded] = useState(false);
  // Photos AND money collection both off → nothing for staff to fill in: skip the dialog
  // and submit the hand-over straight away (backend sends the notification + moves to END).
  const bypass = stationLoaded && collectionOff && !stationPhotosRequired;
  const autoSettled = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // "pickup" = normal branch hand-over; "delivery" = a paid delivery service (e.g.
  // home delivery) that adds a flat service fee on top of the parcels' own shipping
  // fee and sends a "Delivery success" WhatsApp instead of "Pickup success" — see
  // backend controllers/financial.js.
  const [serviceType, setServiceType] = useState("pickup");
  const [deliveryFeeTHB, setDeliveryFeeTHB] = useState(DEFAULT_DELIVERY_FEE_THB);
  // Customer's current wallet balance per currency — shown so staff can tell the
  // customer how much is in the wallet before deducting. null = not loaded / unavailable.
  const [walletBalances, setWalletBalances] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  // null while loading; a missing account counts as inactive (no wallet to charge).
  const [walletActive, setWalletActive] = useState(null);
  // Which currency the wallet portion is debited from — staff can settle from any
  // currency the customer holds a balance in, not just THB.
  const [walletCurrency, setWalletCurrency] = useState("THB");
  // Change given back to the customer for an overpayment — each line is either
  // physical cash from this station's drawer or (only while the wallet is
  // active) a credit to the customer's wallet.
  const [changeLines, setChangeLines] = useState([emptyChangeLine()]);
  // Whether to fold the customer's pre-existing negative wallet balance into the
  // amount staff are prompted to collect right now, defaulting the resulting
  // "change" (overpayment vs. the package fee) to a wallet credit instead of
  // physical cash — i.e. collect today's fee AND pay down old debt in one go.
  // Defaults on; only meaningful (and shown) while the wallet is active, since an
  // inactive wallet can't accept a credit (see applyDefaultChangeSource).
  const [settleDebt, setSettleDebt] = useState(true);

  useEffect(() => {
    if (!open || !customerId) {
      setWalletBalances(null);
      setWalletActive(null);
      return;
    }
    let active = true;
    setBalanceLoading(true);
    getCustomerAccountByAdmin(customerId)
      .then((res) => {
        if (!active) return;
        const found = res && res.success && res.data;
        setWalletBalances(
          found
            ? { THB: Number(res.data.THB) || 0, LAK: Number(res.data.LAK) || 0, USD: Number(res.data.USD) || 0 }
            : null
        );
        setWalletActive(found ? !!res.walletActive : false);
      })
      .catch(() => {
        if (active) setWalletActive(false);
      })
      .finally(() => active && setBalanceLoading(false));
    return () => {
      active = false;
    };
  }, [open, customerId]);

  useEffect(() => {
    if (!open || !customerId) return;
    let active = true;
    GetCustomerInfo(customerId).then((res) => {
      if (!active) return;
      const n = Number(res?.data?.requiredProofPhotos);
      setCustomerRequiredPhotos(Math.min(MAX_PHOTOS, Math.max(1, n || DEFAULT_REQUIRED_PROOF_PHOTOS)));
    });
    return () => {
      active = false;
    };
  }, [open, customerId]);

  useEffect(() => {
    if (!open) {
      setStationLoaded(false);
      return;
    }
    if (!stationId) {
      setStationPhotosRequired(true);
      setCollectionOff(false);
      setStationLoaded(true);
      return;
    }
    let active = true;
    GetStationDetailById(stationId).then((res) => {
      if (!active) return;
      const st = res?.data;
      setStationPhotosRequired(!(st?.finalStation && st.requireHandOverPhotos === false));
      setCollectionOff(!!st?.finalStation && st.collectPayment === false);
      setStationLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [open, stationId]);

  const accent = theme.palette.secondary.main;
  const accentLight = theme.palette.secondary.light;
  const accentDark = theme.palette.secondary.dark;
  const paper = theme.palette.background.paper;
  const BORDER = theme.palette.divider;
  const tint = alpha(accent, 0.06);

  const serviceFeeTHB = serviceType === "delivery" ? Number(deliveryFeeTHB) || 0 : 0;
  const feeTHB = (Number(baseFeeTHB) || 0) + serviceFeeTHB;

  // The fee can change while this dialog is open (delivery service checkbox / fee), so
  // cash/bank amounts prefilled against the old fee would silently shift the difference
  // onto the wallet — drop them and let staff re-enter (same as mobile-staff).
  const lastFeeRef = useRef(feeTHB);
  useEffect(() => {
    if (lastFeeRef.current === feeTHB) return;
    lastFeeRef.current = feeTHB;
    setCash(emptyMethod());
    setBank(emptyMethod());
    setChangeLines([emptyChangeLine()]);
    setError("");
  }, [feeTHB]);

  const cashTHB = methodTHB(cash, currencyExchangeRate);
  const bankTHB = methodTHB(bank, currencyExchangeRate);
  const paidNowTHB = Math.round((cashTHB + bankTHB) * 100) / 100;

  // LAK is paid in physical notes rounded to the nearest 1,000 (see fromTHB above), so a
  // LAK line can land up to 500 LAK (≈0.5-1 THB) off the exact fee. Absorb that unavoidable
  // rounding slack as paid-in-full instead of flagging it as an overpayment — must match
  // the same tolerance the backend applies in collectShippingPayment.
  const roundingToleranceTHB =
    [cash, bank].reduce(
      (n, m) => n + (m.enabled ? m.lines.reduce((sum, l) => sum + paymentRoundingTHB(l.currency, currencyExchangeRate), 0) : 0),
      0
    ) + EPSILON_THB;
  const walletPortionTHB = useMemo(() => {
    // Compare the *unrounded* difference — rounding to cents first can push a value
    // that's exactly at the tolerance boundary just outside it (double-rounding).
    const raw = feeTHB - cashTHB - bankTHB;
    return Math.abs(raw) <= roundingToleranceTHB ? 0 : Math.round(raw * 100) / 100;
  }, [feeTHB, cashTHB, bankTHB, roundingToleranceTHB]);


  // Customer's total pre-existing debt across all currencies, expressed in THB —
  // only meaningful while the wallet is active (an inactive wallet can't accept the
  // credit that pays it down; see applyDefaultChangeSource).
  const debtTHB = useMemo(() => {
    if (!walletBalances || walletActive !== true) return 0;
    return CURRENCIES.reduce((s, c) => {
      const bal = walletBalances[c] || 0;
      return bal < 0 ? s + toTHB(-bal, c, currencyExchangeRate) : s;
    }, 0);
  }, [walletBalances, walletActive, currencyExchangeRate]);
  const settlingDebt = settleDebt && walletActive === true && debtTHB > EPSILON_THB;

  // Overpayment: how much change is owed back to the customer.
  const rawChangeDueTHB = walletPortionTHB < 0 ? -walletPortionTHB : 0;
  // Settling debt: the amount staff should collect is the fee + the debt. A LAK payment
  // lands on note-rounded money (₭913,000 ≈ ฿1,323.19 for a ฿1,322.52 target), so when
  // the overpayment is within that slack of the debt we treat the payment as exactly the
  // target — no leftover "change", the wallet-credit line is the debt itself. The real
  // amounts entered (₭913,000) are still what's submitted and recorded.
  const snapToTarget = settlingDebt && rawChangeDueTHB > 0 && Math.abs(rawChangeDueTHB - debtTHB) <= roundingToleranceTHB;
  const changeDueTHB = snapToTarget ? debtTHB : rawChangeDueTHB;
  // Display-only: when the fee is covered exactly (no wallet shortfall, no change
  // due), snap the "Paid now" figure to the clean fee amount instead of the raw
  // cash/bank sum — which, whenever a LAK line was involved, carries up to ~1 THB
  // of noise from being paid in physical notes rounded to the nearest 1,000 (see
  // roundingToleranceTHB above). Doesn't affect what's actually submitted — the real
  // line amounts (e.g. ₭173,000) are unchanged; this only cleans up the number
  // shown next to "Wallet portion ฿0", which would otherwise look inconsistent
  // (e.g. "Paid now ฿250.72" beside a fee of exactly ฿250).
  const paidNowDisplayTHB = snapToTarget ? feeTHB + debtTHB : (walletPortionTHB === 0 && changeDueTHB === 0 ? feeTHB : paidNowTHB);
  const changeLinesTHB = changeLines.reduce((s, l) => s + toTHB(l.amount, l.currency, currencyExchangeRate), 0);
  const changeLakLineCount = changeLines.filter((l) => l.currency === "LAK" && l.amount).length;
  const changeToleranceTHB =
    (currencyExchangeRate && changeLakLineCount > 0 ? changeLakLineCount * (500 / Number(currencyExchangeRate.LAK)) : 0) +
    EPSILON_THB;
  const changeMismatch = changeDueTHB > 0 && Math.abs(changeLinesTHB - changeDueTHB) > changeToleranceTHB;

  // Recompute the auto-filled last change line whenever the change due shifts, and
  // (re)apply the settle-debt default source to any line staff hasn't manually
  // re-sourced — including retroactively if they toggle the checkbox after a change
  // line has already appeared.
  useEffect(() => {
    if (changeDueTHB <= 0) return;
    setChangeLines((lines) =>
      recomputeAutoChangeLine(applyDefaultChangeSource(lines, settleDebt, walletActive), changeDueTHB, currencyExchangeRate)
    );
  }, [changeDueTHB, currencyExchangeRate, settleDebt, walletActive]);

  function updateChangeLine(idx, field, value) {
    setChangeLines((lines) => {
      const next = lines.map((l, i) =>
        i === idx
          ? {
              ...l,
              [field]: value,
              ...(field === "amount" ? { auto: false } : {}),
              ...(field === "source" ? { sourceAuto: false } : {}),
            }
          : l
      );
      return recomputeAutoChangeLine(next, changeDueTHB, currencyExchangeRate);
    });
  }
  function addChangeLine() {
    setChangeLines((lines) =>
      recomputeAutoChangeLine(
        applyDefaultChangeSource([...lines, emptyChangeLine()], settleDebt, walletActive),
        changeDueTHB,
        currencyExchangeRate
      )
    );
  }
  function removeChangeLine(idx) {
    setChangeLines((lines) => {
      const filtered = lines.filter((_, i) => i !== idx);
      return recomputeAutoChangeLine(filtered.length ? filtered : [emptyChangeLine()], changeDueTHB, currencyExchangeRate);
    });
  }

  // Amount actually debited from the wallet, in the staff-selected currency.
  const walletPortionAmount =
    walletPortionTHB > 0 ? fromTHB(walletPortionTHB, walletCurrency, currencyExchangeRate) : 0;
  const walletBalance = walletBalances ? walletBalances[walletCurrency] : null;

  // Wallet can't cover the auto-charged remainder → backend will route to approval.
  const walletInsufficient =
    walletPortionAmount > 0 && walletBalance != null && walletBalance < walletPortionAmount;
  // Nothing is actually collected right now — no cash/bank AND the wallet can't (or
  // won't) cover it either, so the fee is only recorded as outstanding/pending.
  const noPaymentCollected = paidNowTHB === 0 && (walletActive === false || walletInsufficient);


  // Amount staff are prompted to collect right now — the package fee, plus the
  // customer's outstanding debt when "settle debt" is on. Only affects what cash/bank
  // tiles prefill to; the actual fee owed for these packages (feeTHB) is unchanged, so
  // any excess collected shows up as an overpayment/"change due" exactly like today,
  // just defaulted to a wallet credit instead of physical cash (see the changeDueTHB
  // effect below) — no backend change needed, this reuses the existing changeLines
  // "wallet_credit" mechanism.
  const collectTargetTHB = feeTHB + (settlingDebt ? debtTHB : 0);
  // The hero card at the top shows the whole amount to collect — shipping fee +
  // delivery fee (if ticked) + outstanding debt (if the debt checkbox is ticked).
  const targetLAK = currencyExchangeRate
    ? Math.round((collectTargetTHB * Number(currencyExchangeRate.LAK)) / 1000) * 1000
    : 0;
  const targetUSD = currencyExchangeRate ? (collectTargetTHB / Number(currencyExchangeRate.USD)).toFixed(2) : 0;

  // Split whatever's collected beyond the fee (changeDueTHB) into what actually pays
  // down the customer's debt vs. genuine change owed back — so the UI never calls
  // money that's being kept (as a debt-paydown credit) "change due". The backend
  // itself doesn't need this split; it only validates that changeLines sum to
  // changeDueTHB, however staff choose to label/source each line.
  const debtPaymentTHB = settlingDebt ? Math.min(changeDueTHB, debtTHB) : 0;
  const trueChangeDueTHB = Math.max(0, changeDueTHB - debtPaymentTHB);

  // Amount to prefill into a line = the still-outstanding target (fee [+ debt] minus
  // what the OTHER line already covers), expressed in that line's currency. Enabling
  // the only method therefore fills the full amount due; changing currency reconverts it.
  // `skipIdx` is the line of `which` being (re)filled, so it never counts against
  // itself; pass -1 for a brand-new line.
  const prefillFor = (which, skipIdx, currency) => {
    const sumOthers = (m, isTarget) =>
      m.enabled
        ? m.lines.reduce(
            (s, l, i) => (isTarget && i === skipIdx ? s : s + toTHB(l.amount, l.currency, currencyExchangeRate)),
            0
          )
        : 0;
    const otherTHB = sumOthers(cash, which === "cash") + sumOthers(bank, which === "bank");
    const remaining = Math.max(collectTargetTHB - otherTHB, 0);
    return fromTHB(remaining, currency, currencyExchangeRate);
  };

  const updateLine = (setState, idx, patch) =>
    setState((s) => ({ ...s, lines: s.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  const addLine = (which, state, setState) => {
    const currency = CURRENCIES.find((c) => !state.lines.some((l) => l.currency === c)) || "THB";
    const amount = prefillFor(which, -1, currency);
    setState((s) => ({ ...s, lines: [...s.lines, { ...emptyLine(currency), amount }] }));
  };
  const removeLine = (setState, idx) => setState((s) => ({ ...s, lines: s.lines.filter((_, i) => i !== idx) }));

  const reset = () => {
    setCash(emptyMethod());
    setBank(emptyMethod());
    setProofFiles([]);
    setWalletCurrency("THB");
    setChangeLines([emptyChangeLine()]);
    setSettleDebt(true);
    setServiceType("pickup");
    setDeliveryFeeTHB(DEFAULT_DELIVERY_FEE_THB);
    setError("");
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const validate = () => {
    if (collectionOff) return proofFiles.length < requiredPhotos ? t("pickup.payment.proofPhotosRequired", { count: requiredPhotos }) : "";
    if (changeDueTHB > 0) {
      const validLines = changeLines.filter((l) => l.amount && Number(l.amount) > 0);
      if (validLines.length === 0) return t("pickup.payment.changeLinesRequired");
      if (walletActive === false && validLines.some((l) => l.source === "wallet_credit")) return t("pickup.payment.changeWalletInactive");
      if (changeMismatch) return t("pickup.payment.changeMismatchError");
    }
    if (walletActive === false && walletPortionTHB > 0) return t("pickup.payment.walletInactiveFullPayment");
    if (cash.enabled && cash.lines.some((l) => !l.amount || l.files.length === 0)) return t("pickup.payment.cashIncomplete");
    if (bank.enabled && bank.lines.some((l) => !l.amount || l.files.length === 0)) return t("pickup.payment.bankIncomplete");
    if (proofFiles.length < requiredPhotos) return t("pickup.payment.proofPhotosRequired", { count: requiredPhotos });
    return "";
  };

  // Build the breakdown + contextual note shown in the confirmation popup.
  const buildConfirmHtml = () => {
    const fmt = (n) => `฿${Number(n || 0).toLocaleString()}`;
    const row = (label, formattedValue, color) =>
      `<div style="display:flex;justify-content:space-between;gap:16px;padding:5px 0;">
         <span style="color:#6b6577;font-size:13px;">${label}</span>
         <span style="font-weight:700;font-size:13.5px;${color ? `color:${color};` : ""}">${formattedValue}</span>
       </div>`;
    let html = `<div style="text-align:left;margin-top:2px;">`;
    html += row(t("pickup.payment.feeDue"), fmt(feeTHB));
    html += row(t("pickup.payment.paidNow"), fmt(paidNowDisplayTHB));
    if (walletPortionAmount > 0)
      html += row(
        t("pickup.payment.walletPortion") + (walletCurrency !== "THB" ? ` (${walletCurrency})` : ""),
        fmtCur(walletPortionAmount, walletCurrency),
        theme.palette.warning.main
      );
    if (changeDueTHB > 0)
      html += row(
        debtPaymentTHB > EPSILON_THB
          ? trueChangeDueTHB > EPSILON_THB
            ? t("pickup.payment.debtAndChangeLabel")
            : t("pickup.payment.appliedToDebtLabel")
          : t("pickup.payment.changeGiven"),
        changeLines
          .filter((l) => l.amount && Number(l.amount) > 0)
          .map(
            (l) =>
              fmtCur(Number(l.amount), l.currency) +
              (l.source === "wallet_credit" ? ` (${t("pickup.payment.changeWallet")})` : "")
          )
          .join(" + "),
        trueChangeDueTHB > EPSILON_THB ? theme.palette.warning.main : accent
      );
    html += `</div>`;

    let note = "";
    if (walletPortionAmount > 0 && walletInsufficient)
      note = t("pickup.payment.confirmWalletDebtNote", {
        amount: Number(walletPortionAmount).toLocaleString(),
        currencySymbol: SYM[walletCurrency] || "",
      });
    else if (walletPortionAmount > 0)
      note = t("pickup.payment.confirmWalletChargeNote", {
        amount: Number(walletPortionAmount).toLocaleString(),
        currencySymbol: SYM[walletCurrency] || "",
      });
    else if (debtPaymentTHB > EPSILON_THB) {
      const remainingDebtTHB = Math.max(0, debtTHB - debtPaymentTHB);
      note =
        remainingDebtTHB > EPSILON_THB
          ? t("pickup.payment.remainingDebtNote", { amount: remainingDebtTHB.toLocaleString(undefined, { maximumFractionDigits: 2 }) })
          : t("pickup.payment.debtClearedNote");
    }
    if (note)
      html += `<div style="margin-top:12px;padding:10px 12px;border-radius:10px;background:${alpha(
        theme.palette.warning.main,
        0.1
      )};color:#7a4f00;font-size:12.5px;line-height:1.5;text-align:left;">${note}</div>`;
    return html;
  };

  // Validate, then ask for confirmation before actually collecting. The popup is
  // fired while the (MUI) dialog is open; the global `.swal2-container` z-index
  // (2000, index.css) already sits above the MUI modal (1300).
  const handleCollectClick = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError("");
    if (collectionOff) {
      doCollect();
      return;
    }
    const result = await Swal.fire({
      title: t("pickup.payment.confirmTitle"),
      html: buildConfirmHtml(),
      icon: walletPortionTHB > 0 && walletInsufficient ? "warning" : "question",
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: t("pickup.payment.confirmYes"),
      cancelButtonText: t("common.cancel"),
      confirmButtonColor: accent,
    });
    if (result.isConfirmed) doCollect();
  };

  const doCollect = async () => {
    setSubmitting(true);
    setError("");

    const lines = [];
    const fd = new FormData();
    const addLines = (state, method) => {
      if (!state.enabled) return;
      state.lines.forEach((l) => {
        const idx = lines.length;
        lines.push({ method, currency: l.currency, amount: Number(l.amount), evidenceField: `evidence_${idx}` });
        l.files.forEach((f) => fd.append(`evidence_${idx}`, f));
      });
    };
    addLines(cash, "cash");
    addLines(bank, "bank_transfer");
    proofFiles.forEach((f) => fd.append("deliveryProofPhoto", f));
    fd.append("customerId", customerId);
    fd.append("stationId", stationId);
    fd.append("packageIds", JSON.stringify(packageIds));
    fd.append("lines", JSON.stringify(lines));
    fd.append("walletCurrency", walletCurrency);
    if (changeDueTHB > 0) {
      const validChangeLines = changeLines
        .filter((l) => l.amount && Number(l.amount) > 0)
        .map((l) => ({ source: l.source, currency: l.currency, amount: Number(l.amount) }));
      fd.append("changeLines", JSON.stringify(validChangeLines));
    }
    if (serviceType) fd.append("serviceType", serviceType);
    if (serviceFeeTHB) fd.append("serviceFeeTHB", serviceFeeTHB);

    const res = await collectShippingPayment(fd);
    setSubmitting(false);
    if (res && res.success) {
      reset();
      onSuccess(res.status); // 'completed' | 'pending_approval'
    } else {
      playIncorrectSound();
      const message = (res && res.message) || t("common.error");
      setError(message);
      if (bypass) {
        // The dialog was never shown, so surface the failure and close.
        Swal.fire({ icon: "error", text: message });
        onClose();
      }
    }
  };

  useEffect(() => {
    if (!open) {
      autoSettled.current = false;
      return;
    }
    if (bypass && !autoSettled.current) {
      autoSettled.current = true;
      doCollect();
    }
  }, [open, bypass]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── A toggleable payment-method tile (cash / bank). Header toggles the method;
  // the inputs only render when it is switched on. ──────────────────────────────
  const methodTile = (which, state, setState, label, Icon) => {
    const on = state.enabled;
    return (
      <Box
        sx={{
          border: `1.5px solid ${on ? alpha(accent, 0.5) : BORDER}`,
          background: on ? tint : paper,
          borderRadius: "16px",
          overflow: "hidden",
          transition: "border-color .15s, background .15s",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          onClick={() =>
            setState((s) => {
              const enabled = !s.enabled;
              // Prefill the outstanding amount into the first line when switching the method on.
              return enabled
                ? {
                    ...s,
                    enabled,
                    lines: s.lines.map((l, i) => (i === 0 ? { ...l, amount: prefillFor(which, 0, l.currency) } : l)),
                  }
                : { ...s, enabled };
            })
          }
          sx={{ cursor: "pointer", px: 1.75, py: 1.5, userSelect: "none" }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "11px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: on ? `linear-gradient(135deg, ${accentLight}, ${accentDark})` : alpha(accent, 0.1),
                color: on ? "#fff" : accent,
              }}
            >
              <Icon sx={{ fontSize: 20 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }} color={on ? "secondary.main" : "text.primary"}>
                {label}
              </Typography>
              {!on && (
                <Typography sx={{ fontSize: 11.5 }} color="text.secondary">
                  {t("pickup.payment.tapToAdd")}
                </Typography>
              )}
            </Box>
          </Stack>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${on ? accent : alpha(theme.palette.text.secondary, 0.4)}`,
              background: on ? accent : "transparent",
            }}
          >
            {on && <CheckRoundedIcon sx={{ fontSize: 15, color: "#fff" }} />}
          </Box>
        </Stack>

        {on && (
          <Box sx={{ px: 1.75, pb: 1.75, pt: 0.25 }}>
            {state.lines.map((line, idx) => (
              <Grid
                container
                spacing={1}
                key={idx}
                sx={idx > 0 ? { mt: 0.5, pt: 1, borderTop: `1px dashed ${alpha(accent, 0.4)}` } : undefined}
              >
                <Grid item xs={state.lines.length > 1 ? 3.5 : 4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={t("pickup.payment.currency")}
                    value={line.currency}
                    onChange={(e) => {
                      const currency = e.target.value;
                      // Reconvert the outstanding amount into the new currency.
                      updateLine(setState, idx, { currency, amount: prefillFor(which, idx, currency) });
                    }}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={state.lines.length > 1 ? 3.5 : 4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t("pickup.payment.amount")}
                    value={line.amount}
                    onChange={(e) => updateLine(setState, idx, { amount: e.target.value })}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant={line.files.length ? "contained" : "outlined"}
                    color="secondary"
                    component="label"
                    fullWidth
                    size="small"
                    disabled={line.files.length >= MAX_PHOTOS}
                    startIcon={line.files.length ? <CheckRoundedIcon /> : <PhotoCameraOutlinedIcon />}
                    sx={{ height: "40px", textTransform: "none", fontWeight: 600 }}
                  >
                    {line.files.length
                      ? t("pickup.payment.photosAdded", { count: line.files.length })
                      : t("pickup.payment.attachPhoto")}
                    <input
                      hidden
                      accept="image/*"
                      type="file"
                      multiple
                      onChange={(e) => {
                        updateLine(setState, idx, { files: appendFiles(line.files, e.target.files) });
                        e.target.value = "";
                      }}
                    />
                  </Button>
                </Grid>
                {state.lines.length > 1 && (
                  <Grid item xs={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconButton size="small" color="error" onClick={() => removeLine(setState, idx)}>
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  </Grid>
                )}
                {line.files.length > 0 && (
                  <Grid item xs={12}>
                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                      {line.files.map((f, i) => (
                        <Chip
                          key={`${f.name}-${i}`}
                          size="small"
                          label={f.name}
                          onDelete={() => updateLine(setState, idx, { files: line.files.filter((_, j) => j !== i) })}
                          sx={{ maxWidth: 160 }}
                        />
                      ))}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            ))}
            {state.lines.length < CURRENCIES.length && (
              <Button
                size="small"
                variant="text"
                color="secondary"
                startIcon={<AddRoundedIcon />}
                onClick={() => addLine(which, state, setState)}
                sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
              >
                {t("pickup.payment.addPayment")}
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  };

  const allocTile = ({ label, value, sub, icon: Icon, highlight }) => (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1.75,
        borderRadius: "16px",
        border: `1px solid ${highlight ? alpha(theme.palette.warning.main, 0.4) : BORDER}`,
        background: highlight ? alpha(theme.palette.warning.main, 0.08) : paper,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
        <Icon sx={{ fontSize: 16, color: highlight ? theme.palette.warning.main : accent }} />
        <Typography
          sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}
          color="text.secondary"
        >
          {label}
        </Typography>
      </Stack>
      <Typography
        sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}
        color={highlight ? "warning.main" : "text.primary"}
      >
        ฿{value.toLocaleString()}
      </Typography>
      {sub && (
        <Typography sx={{ fontSize: 11, mt: 0.25 }} color="text.secondary">
          {sub}
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog open={open && stationLoaded && !bypass} onClose={handleClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: "22px" } }}
    >
      <DialogContent sx={{ p: { xs: 2.25, sm: 3 } }}>
        {/* title */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 19 }} color="text.primary">
            {t("pickup.payment.title")}
          </Typography>
          <Typography sx={{ fontSize: 12.5, mt: 0.25 }} color="text.secondary">
            {t("pickup.payment.subtitle")}
          </Typography>
          {collectionOff && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: "12px" }}>
              {t("pickup.payment.collectionOffNote")}
            </Alert>
          )}
        </Box>

        {!collectionOff && (<>
        {/* fee-due hero tile */}
        <Box
          sx={{
            borderRadius: "18px",
            p: "18px 20px",
            color: "#fff",
            background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
            boxShadow: `0 10px 24px -12px ${alpha(accent, 0.7)}`,
          }}
        >
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", opacity: 0.85 }}>
            {t("pickup.payment.totalToCollect")}
          </Typography>
          {/* Three equal-size currency boxes — THB/LAK/USD all read at the same size
              so staff quoting a price in Kip aren't stuck reading a tiny sub-line
              (was one large THB value + a small "₭… · $…" line). */}
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            {[
              { label: "THB", value: `฿${collectTargetTHB.toLocaleString(undefined, { maximumFractionDigits: 2 })}` },
              { label: "LAK", value: `₭${targetLAK.toLocaleString()}` },
              { label: "USD", value: `$${targetUSD}` },
            ].map((c) => (
              <Box key={c.label} sx={{ flex: 1, borderRadius: "11px", bgcolor: "rgba(255,255,255,0.14)", px: 1.25, py: 1 }}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".05em", opacity: 0.7 }}>
                  {c.label}
                </Typography>
                <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 16, mt: 0.25, letterSpacing: "-0.02em" }}>
                  {c.value}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* optional delivery service (home delivery etc.) — same idea as the debt
            checkbox below: ticking it adds an editable flat fee (default ฿40) on top of
            the shipping fee and sends "Delivery success" instead of "Pickup success". */}
        <Box
          sx={{
            mt: 1.5,
            p: "10px 14px",
            borderRadius: "14px",
            border: `1px solid ${alpha(accent, 0.35)}`,
            background: alpha(accent, 0.06),
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              onClick={() => setServiceType((v) => (v === "delivery" ? "pickup" : "delivery"))}
              sx={{ cursor: "pointer", flex: 1, minWidth: 0 }}
            >
              <Checkbox
                checked={serviceType === "delivery"}
                onChange={(e) => setServiceType(e.target.checked ? "delivery" : "pickup")}
                onClick={(e) => e.stopPropagation()}
                color="secondary"
                size="small"
                sx={{ p: 0.5 }}
              />
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: 12.5 }} color="secondary.main">
                  {t("pickup.payment.deliveryServiceLabel")}
                </Typography>
                <Typography sx={{ fontSize: 11 }} color="text.secondary">
                  {t("pickup.payment.deliveryServiceHint")}
                </Typography>
              </Box>
            </Stack>
            {serviceType === "delivery" && (
              <TextField
                size="small"
                type="number"
                label={t("pickup.selfPickup.deliveryFee")}
                value={deliveryFeeTHB}
                onChange={(e) => setDeliveryFeeTHB(e.target.value)}
                InputProps={{ startAdornment: "฿" }}
                sx={{ width: 130, flexShrink: 0, "& .MuiInputBase-root": { height: 40, borderRadius: "10px" } }}
              />
            )}
          </Stack>
        </Box>

        {/* settle outstanding wallet debt — only relevant while the wallet is
            active and actually negative; folds the debt into what staff are
            prompted to collect, defaulting any resulting overpayment to a wallet
            credit (see collectTargetTHB / applyDefaultChangeSource above). */}
        {walletActive === true && debtTHB > EPSILON_THB && (
          <Box
            onClick={() => setSettleDebt((v) => !v)}
            sx={{
              mt: 1.5,
              p: "10px 14px",
              borderRadius: "14px",
              border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
              background: alpha(theme.palette.error.main, 0.06),
              cursor: "pointer",
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Checkbox
                  checked={settleDebt}
                  onChange={(e) => setSettleDebt(e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  color="secondary"
                  size="small"
                  sx={{ p: 0.5 }}
                />
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 12.5 }} color="error.main">
                    {t("pickup.payment.settleDebtLabel")}
                  </Typography>
                  <Typography sx={{ fontSize: 11 }} color="text.secondary">
                    {t("pickup.payment.settleDebtHint")}
                  </Typography>
                </Box>
              </Stack>
              <Typography sx={{ fontFamily: MONO, fontWeight: 800, fontSize: 16, flexShrink: 0 }} color="error.main">
                ฿{debtTHB.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* payment methods */}
        <Typography
          sx={{ mt: 2.25, mb: 1, fontSize: 11.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}
          color="text.secondary"
        >
          {t("pickup.payment.paymentMethods")}
        </Typography>
        <Stack spacing={1.25}>
          {methodTile("cash", cash, setCash, t("pickup.payment.cash"), PaymentsOutlinedIcon)}
          {methodTile("bank", bank, setBank, t("pickup.payment.bankTransfer"), AccountBalanceOutlinedIcon)}
        </Stack>
        </>)}

        {/* hand-over photos (parcel handed over + person taking it) — one upload button,
            the customer's required count applies regardless of payment method */}
        <Typography
          sx={{ mt: 2, mb: 0.5, fontSize: 11.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}
          color="text.secondary"
        >
          {t("pickup.payment.proofPhotos")} · {requiredPhotos > 0 ? `${proofFiles.length}/${requiredPhotos}` : t("pickup.payment.proofPhotosOptional")}
        </Typography>
        <Typography sx={{ fontSize: 11.5, mb: 1 }} color="text.secondary">
          {t("pickup.payment.proofPhotosHint")}
        </Typography>
        <Button
          variant={proofFiles.length > 0 && proofFiles.length >= requiredPhotos ? "contained" : "outlined"}
          color="secondary"
          component="label"
          fullWidth
          disabled={proofFiles.length >= MAX_PHOTOS}
          startIcon={proofFiles.length ? <CheckRoundedIcon /> : <PhotoCameraOutlinedIcon />}
          sx={{ height: "44px", textTransform: "none", fontWeight: 600 }}
        >
          {proofFiles.length
            ? t("pickup.payment.photosAdded", { count: proofFiles.length })
            : t("pickup.payment.attachPhoto")}
          <input
            hidden
            accept="image/*"
            type="file"
            multiple
            onChange={(e) => {
              setProofFiles((files) => appendFiles(files, e.target.files));
              e.target.value = "";
            }}
          />
        </Button>
        {proofFiles.length > 0 && (
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
            {proofFiles.map((f, i) => (
              <Chip
                key={`${f.name}-${i}`}
                size="small"
                label={f.name}
                onDelete={() => setProofFiles((files) => files.filter((_, j) => j !== i))}
                sx={{ maxWidth: 200 }}
              />
            ))}
          </Stack>
        )}
        {proofFiles.length >= MAX_PHOTOS && (
          <Typography sx={{ fontSize: 11, mt: 0.5 }} color="text.secondary">
            {t("pickup.payment.maxPhotosReached")}
          </Typography>
        )}

        {!collectionOff && (<>
        <Divider sx={{ my: 2 }} />

        {/* allocation breakdown — Paid now vs Wallet (auto, with current balance) */}
        <Stack direction="row" spacing={1.25} alignItems="stretch">
          {allocTile({
            label: t("pickup.payment.paidNow"),
            value: paidNowDisplayTHB,
            icon: PaymentsOutlinedIcon,
          })}
          {/* Wallet portion (auto remainder) — replaced by a Change Due / Applied-to-
              debt tile when the customer paid more than the fee. Purely debt-settling
              (no genuine change) uses the accent color rather than the warning color,
              since nothing is actually being handed back — it's expected, not a flag. */}
          {changeDueTHB > 0 ? (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.75,
                borderRadius: "16px",
                border: `1px solid ${alpha(
                  trueChangeDueTHB > EPSILON_THB ? theme.palette.warning.main : accent,
                  0.4
                )}`,
                background: alpha(trueChangeDueTHB > EPSILON_THB ? theme.palette.warning.main : accent, 0.08),
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
                <AccountBalanceWalletOutlinedIcon
                  sx={{ fontSize: 16, color: trueChangeDueTHB > EPSILON_THB ? theme.palette.warning.main : accent }}
                />
                <Typography
                  sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}
                  color="text.secondary"
                >
                  {debtPaymentTHB > EPSILON_THB
                    ? trueChangeDueTHB > EPSILON_THB
                      ? t("pickup.payment.debtAndChangeLabel")
                      : t("pickup.payment.appliedToDebtLabel")
                    : t("pickup.payment.changeDueLabel")}
                </Typography>
              </Stack>
              <Typography
                sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}
                color={trueChangeDueTHB > EPSILON_THB ? "warning.main" : "secondary.main"}
              >
                ฿{changeDueTHB.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </Typography>
              {debtPaymentTHB > EPSILON_THB && trueChangeDueTHB > EPSILON_THB && (
                <Typography sx={{ fontSize: 10.5, mt: 0.25 }} color="text.secondary">
                  {t("pickup.payment.debtChangeBreakdown", {
                    debt: debtPaymentTHB.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                    change: trueChangeDueTHB.toLocaleString(undefined, { maximumFractionDigits: 2 }),
                  })}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                p: 1.75,
                borderRadius: "16px",
                border: `1px solid ${
                  walletPortionTHB > 0 ? alpha(theme.palette.warning.main, 0.4) : BORDER
                }`,
                background: walletPortionTHB > 0 ? alpha(theme.palette.warning.main, 0.08) : paper,
              }}
            >
              <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <AccountBalanceWalletOutlinedIcon
                    sx={{ fontSize: 16, color: walletPortionTHB > 0 ? theme.palette.warning.main : accent }}
                  />
                  <Typography
                    sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}
                    color="text.secondary"
                  >
                    {t("pickup.payment.walletPortion")}
                  </Typography>
                </Stack>
                {walletActive !== false && (
                  <TextField
                    select
                    size="small"
                    variant="standard"
                    value={walletCurrency}
                    onChange={(e) => setWalletCurrency(e.target.value)}
                    SelectProps={{ disableUnderline: true }}
                    sx={{
                      minWidth: 42,
                      "& .MuiSelect-select": {
                        fontSize: 11,
                        fontWeight: 700,
                        color: accent,
                        py: 0,
                        pr: "20px !important",
                      },
                    }}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>
              <Typography
                sx={{ fontFamily: MONO, fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}
                color={walletActive === false && walletPortionTHB > 0 ? "error.main" : walletPortionTHB > 0 ? "warning.main" : "text.primary"}
              >
                {fmtCur(Math.max(walletPortionAmount, 0), walletCurrency)}
              </Typography>
              <Typography sx={{ fontSize: 11, mt: 0.25 }} color="text.secondary">
                {walletActive === false ? t("pickup.payment.walletInactive") : t("pickup.payment.walletAuto")}
              </Typography>

              {/* current wallet balance — hidden when the wallet is inactive (nothing to charge) */}
              {walletActive !== false && (
                <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${BORDER}` }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography sx={{ fontSize: 11.5 }} color="text.secondary">
                      {t("pickup.payment.balance")}
                    </Typography>
                    <Typography
                      sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 700 }}
                      color={walletInsufficient ? "error.main" : "text.primary"}
                    >
                      {balanceLoading
                        ? "…"
                        : walletBalance == null
                        ? "—"
                        : fmtCur(walletBalance, walletCurrency)}
                    </Typography>
                  </Stack>
                  {walletInsufficient && (
                    <Typography sx={{ fontSize: 10.5, mt: 0.25 }} color="error.main">
                      {t("pickup.payment.insufficientApproval")}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Stack>

        {/* Amount collected beyond the fee — shown whenever cash/bank collected
            exceeds the fee due. Each line is either cash from this station's drawer
            or a credit to the customer wallet; while settling debt, the wallet-credit
            lines pay down the debt rather than being handed back as change. */}
        {changeDueTHB > 0 && (
          <Box
            sx={{
              mt: 1.5,
              p: 1.5,
              borderRadius: "14px",
              border: `1px solid ${alpha(trueChangeDueTHB > EPSILON_THB ? theme.palette.warning.main : accent, 0.35)}`,
              background: alpha(trueChangeDueTHB > EPSILON_THB ? theme.palette.warning.main : accent, 0.05),
            }}
          >
            <Typography sx={{ fontSize: 11.5, mb: 1 }} color="text.secondary">
              {debtPaymentTHB > EPSILON_THB
                ? trueChangeDueTHB > EPSILON_THB
                  ? t("pickup.payment.debtAndChangeHint")
                  : t("pickup.payment.debtSettleHint")
                : t("pickup.payment.giveChangeHint")}
            </Typography>
            <Stack spacing={1}>
              {changeLines.map((line, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <TextField
                    select
                    size="small"
                    value={line.source}
                    onChange={(e) => updateChangeLine(idx, "source", e.target.value)}
                    sx={{ width: 132, flexShrink: 0 }}
                  >
                    <MenuItem value="cash">{t("pickup.payment.changeCash")}</MenuItem>
                    {walletActive !== false && <MenuItem value="wallet_credit">{t("pickup.payment.changeWallet")}</MenuItem>}
                  </TextField>
                  <TextField
                    select
                    size="small"
                    value={line.currency}
                    onChange={(e) => updateChangeLine(idx, "currency", e.target.value)}
                    sx={{ width: 88, flexShrink: 0 }}
                  >
                    {CURRENCIES.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t("pickup.payment.amount")}
                    value={line.amount}
                    onChange={(e) => updateChangeLine(idx, "amount", e.target.value)}
                  />
                  <IconButton size="small" onClick={() => removeChangeLine(idx)} disabled={changeLines.length === 1}>
                    <CloseRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
            <Button
              size="small"
              variant="text"
              color="secondary"
              startIcon={<AddRoundedIcon />}
              onClick={addChangeLine}
              sx={{ mt: 1, textTransform: "none", fontWeight: 700 }}
            >
              {t("pickup.payment.addChangeLine")}
            </Button>
            {changeMismatch && (
              <Typography sx={{ fontSize: 11, mt: 0.5 }} color="error.main">
                {t("pickup.payment.changeMismatch")}
              </Typography>
            )}
          </Box>
        )}

        {walletActive === false && walletPortionTHB > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5, borderRadius: "12px" }}>
            {t("pickup.payment.walletInactiveFullPayment")}
          </Alert>
        )}
        {walletActive !== false && walletPortionTHB > 0 && (
          <Alert severity="info" sx={{ mt: 1.5, borderRadius: "12px" }}>
            {t("pickup.payment.walletNote")}
          </Alert>
        )}
        </>)}
        {error && (
          <Alert severity="error" sx={{ mt: 1.5, borderRadius: "12px" }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.25, sm: 3 }, pb: 2.5, pt: 0 }}>
        <Button variant="outlined" color="secondary" onClick={handleClose} disabled={submitting}
          sx={{ borderRadius: "12px", fontWeight: 700, px: 2.5 }}
        >
          {t("common.cancel")}
        </Button>
        <Button variant="contained" color="secondary" onClick={handleCollectClick} disabled={submitting}
          sx={{
            borderRadius: "12px",
            fontWeight: 700,
            px: 3,
            background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`,
            boxShadow: `0 4px 14px -4px ${alpha(accent, 0.6)}`,
            "&:hover": { background: `linear-gradient(135deg, ${accentLight}, ${accentDark})`, opacity: 0.92 },
          }}
        >
          {!collectionOff && noPaymentCollected
            ? t("pickup.payment.collectWithoutPayment")
            : t("pickup.payment.collect")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
